import { getSynthesizedListingPrices } from '@ps/functions/utils/listing-price';

import { isAccessGated } from '../../shared/functions/seriesClipAccess';
import type { CourseDetailViewModel } from '../types/SeriesDetailViewModels';

/**
 * The two independent unlock paths a course offers, derived from its editor
 * config — read by the visitor "Unlock this course" banner. A PURE render-time
 * derivation from the course's SOURCE primitives; nothing viewer-dependent (the
 * banner layers the viewer's standing on top).
 */
export interface CourseUnlockConfig {
  /**
   * One-shot purchase is offered — the creator enabled "sell standalone" with a
   * `price`, and the backend minted the derived `listingId` that is bought.
   */
  purchasable: boolean;
  /** The price to charge (discount-aware); `null` when not purchasable. */
  effectivePrice: number | null;
  /** The pre-discount price to strike through, or `null` when not discounted. */
  originalPrice: number | null;
  /** The course is gated behind a creator subscription tier (`accessLevel !== all`). */
  subscriptionGated: boolean;
  /** The tier that unlocks it (`subscriber` | `fan` | `vip`), or `undefined` if ungated. */
  requiredAccessLevel: string | undefined;
}

/**
 * Whether the course is sold standalone for a PAID one-shot purchase — the creator
 * enabled "sell standalone" (`standaloneSale` defaults on; `!== false`), the price
 * is positive (a $0 course is free, not purchasable), and the backend minted the
 * derived `listingId` that is bought. Date-free (the discount window is a display
 * concern handled in {@link courseUnlockConfig}).
 */
export function isCoursePurchasable(course: CourseDetailViewModel): boolean {
  return (
    course.standaloneSale !== false &&
    course.price != null &&
    course.price > 0 &&
    course.listingId != null
  );
}

/**
 * Whether the course offers ANY unlock path — a paid standalone purchase OR a
 * subscription gate. A FREE, UNGATED course offers neither (anyone can watch), so
 * the "Unlock this course" banner shouldn't render at all. Date-free, so callers
 * can gate on it in a server component before mounting the client island.
 */
export function courseOffersUnlock(course: CourseDetailViewModel): boolean {
  return isCoursePurchasable(course) || isAccessGated(course.accessLevel);
}

export function courseUnlockConfig(
  course: CourseDetailViewModel,
): CourseUnlockConfig {
  const purchasable = isCoursePurchasable(course);

  // Synthesize the active price via the shared util so the discount window
  // (start/end dates) is honored — matching the server-side `getCourseInfo`.
  let effectivePrice: number | null = null;
  let originalPrice: number | null = null;
  if (purchasable) {
    const { priceRange, discountPriceRange } = getSynthesizedListingPrices(
      {
        price: course.price as number,
        discountPrice: course.discountPrice,
        discountEnabled: course.discountEnabled,
        discountStartDate: course.discountStartDate,
        discountEndDate: course.discountEndDate,
      },
      { prefer: 'listing' },
    );
    const base = priceRange?.min ?? (course.price as number);
    const discounted = discountPriceRange?.min ?? null;
    effectivePrice = discounted ?? base;
    originalPrice = discounted != null ? base : null;
  }

  return {
    purchasable,
    effectivePrice,
    originalPrice,
    subscriptionGated: isAccessGated(course.accessLevel),
    requiredAccessLevel: course.accessLevel,
  };
}
